import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Database, Search, Download, AlertTriangle, CheckSquare, Square, XSquare, Columns, Upload, FileSpreadsheet, X, Briefcase, RefreshCw, Eye, Check, Maximize2, Minimize2 } from 'lucide-react';
import ColumnSelector from '../components/ColumnSelector';

const SEVERITIES = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA'];

// Definición de columnas disponibles para Movimientos
const DEFAULT_COLUMNS = [
  { key: 'id_waya', label: 'ID Waya' },
  { key: 'fecha', label: 'Fecha' },
  { key: 'monto', label: 'Monto' },
  { key: 'cuit', label: 'CUIT' },
  { key: 'movimiento', label: 'Movimiento' },
  { key: 'cvu_destino', label: 'CVU Destino' },
  { key: 'cbu_origen', label: 'CBU Origen' },
  { key: 'descripcion', label: 'Descripción' },
];

export const Movimientos = () => {
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchField, setSearchField] = useState('cuit');
  const [searchValue, setSearchValue] = useState('');
  const [dateFilters, setDateFilters] = useState({
    fecha_desde: '',
    fecha_hasta: '',
  });
  
  // Columnas visibles (configurables)
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
  
  // Selección múltiple
  const [selectedMovimientos, setSelectedMovimientos] = useState(new Set());
  
  // Modal de crear alerta
  const [isCreateAlertOpen, setIsCreateAlertOpen] = useState(false);
  const [savingAlert, setSavingAlert] = useState(false);
  const [newAlert, setNewAlert] = useState({
    titulo: '',
    descripcion: '',
    severidad: 'MEDIA'
  });

  // Modal de crear caso
  const [isCreateCaseOpen, setIsCreateCaseOpen] = useState(false);
  const [savingCase, setSavingCase] = useState(false);
  const [newCase, setNewCase] = useState({
    titulo: '',
    descripcion: '',
    prioridad: 'MEDIA',
    tipo: 'ALERTA'
  });

  // Modal de importar Excel
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importMode, setImportMode] = useState('import'); // 'import' o 'update'
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Fullscreen mode
  const [isFullscreen, setIsFullscreen] = useState(false);

  const searchFields = [
    { value: 'cuit', label: 'CUIT' },
    { value: 'cbu_origen', label: 'CBU Origen' },
    { value: 'cbu_destino', label: 'CBU Destino' },
    { value: 'id_waya', label: 'ID Waya' },
  ];

  // Callback cuando cambian las columnas
  const handleColumnsChange = (columns) => {
    setVisibleColumns(columns.length > 0 ? columns : DEFAULT_COLUMNS);
  };

  // Cargar movimientos recientes al inicio
  useEffect(() => {
    loadRecentMovimientos();
  }, []);

  const loadRecentMovimientos = async () => {
    setLoading(true);
    try {
      const response = await api.get('/movimientos', { params: { limit: 50 } });
      setMovimientos(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setSelectedMovimientos(new Set());
    try {
      const params = { limit: 100 };
      
      if (dateFilters.fecha_desde) params.fecha_desde = dateFilters.fecha_desde;
      if (dateFilters.fecha_hasta) params.fecha_hasta = dateFilters.fecha_hasta;
      
      if (searchValue.trim()) {
        params[searchField] = searchValue.trim();
      }

      const response = await api.get('/movimientos', { params });
      setMovimientos(response.data);
      toast.success(`${response.data.length} movimientos encontrados`);
    } catch (error) {
      toast.error('Error al buscar movimientos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleExportCSV = async () => {
    try {
      toast.info('Exportando movimientos a CSV...');
      const params = {};
      if (searchValue.trim()) {
        params.search_by = searchField;
        params.search_term = searchValue.trim();
      }
      
      const response = await api.get('/exports/movimientos/csv', {
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `movimientos_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Movimientos exportados correctamente');
    } catch (error) {
      toast.error('Error al exportar movimientos');
    }
  };

  // Función para importar/actualizar Excel
  const handleImportExcel = async () => {
    if (!importFile) {
      toast.error('Seleccione un archivo Excel');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      // Usar endpoint según el modo
      const endpoint = importMode === 'import' 
        ? '/movimientos/import/excel' 
        : '/movimientos/update/excel';

      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult(response.data);
      
      if (response.data.success) {
        if (importMode === 'import') {
          toast.success(`Importación completada: ${response.data.inserted} nuevos, ${response.data.skipped} omitidos`);
        } else {
          toast.success(`Actualización completada: ${response.data.updated} actualizados, ${response.data.skipped} no encontrados`);
        }
        loadRecentMovimientos();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al procesar archivo';
      toast.error(errorMsg);
      setImportResult({ success: false, message: errorMsg });
    } finally {
      setImporting(false);
    }
  };

  const openImportModal = (mode) => {
    setImportMode(mode);
    setIsImportOpen(true);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Solo se permiten archivos Excel (.xlsx, .xls)');
        return;
      }
      setImportFile(file);
      setImportResult(null);
      setPreviewData(null);
      
      // Cargar vista previa automáticamente
      setLoadingPreview(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/movimientos/preview/excel', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setPreviewData(response.data);
      } catch (error) {
        console.error('Error loading preview:', error);
        toast.error('Error al cargar vista previa del archivo');
      } finally {
        setLoadingPreview(false);
      }
    }
  };

  const closeImportModal = () => {
    setIsImportOpen(false);
    setImportFile(null);
    setImportResult(null);
    setPreviewData(null);
  };

  // Funciones de selección
  const toggleSelection = (id) => {
    const newSelection = new Set(selectedMovimientos);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedMovimientos(newSelection);
  };

  const selectAll = () => {
    const allIds = new Set(movimientos.map(m => m.id_waya));
    setSelectedMovimientos(allIds);
  };

  const deselectAll = () => {
    setSelectedMovimientos(new Set());
  };

  const openCreateAlert = () => {
    if (selectedMovimientos.size === 0) {
      toast.error('Seleccione al menos un movimiento');
      return;
    }
    
    // Pre-llenar el formulario con datos de los movimientos seleccionados
    const selected = movimientos.filter(m => selectedMovimientos.has(m.id_waya));
    const totalMonto = selected.reduce((sum, m) => sum + (parseFloat(m.monto) || 0), 0);
    const cuits = [...new Set(selected.map(m => m.cuit).filter(Boolean))];
    
    setNewAlert({
      titulo: `Alerta sobre ${selected.length} movimiento(s)`,
      descripcion: `Movimientos seleccionados:\n- Total: $${totalMonto.toLocaleString()}\n- CUIT(s): ${cuits.join(', ') || 'N/A'}\n- IDs: ${Array.from(selectedMovimientos).slice(0, 5).join(', ')}${selectedMovimientos.size > 5 ? '...' : ''}`,
      severidad: 'MEDIA'
    });
    setIsCreateAlertOpen(true);
  };

  const handleCreateAlert = async () => {
    if (!newAlert.titulo.trim()) {
      toast.error('El título es requerido');
      return;
    }

    setSavingAlert(true);
    try {
      const selected = movimientos.filter(m => selectedMovimientos.has(m.id_waya));
      const cuits = [...new Set(selected.map(m => m.cuit).filter(Boolean))];
      
      await api.post('/alerts/', {
        titulo: newAlert.titulo,
        descripcion: newAlert.descripcion,
        severidad: newAlert.severidad,
        cuenta_id: cuits[0] || null,
        movimientos_ids: Array.from(selectedMovimientos).map(String)
      });
      
      toast.success('Alerta creada correctamente');
      setIsCreateAlertOpen(false);
      setSelectedMovimientos(new Set());
      setNewAlert({ titulo: '', descripcion: '', severidad: 'MEDIA' });
    } catch (error) {
      toast.error('Error al crear alerta');
    } finally {
      setSavingAlert(false);
    }
  };

  const openCreateCaseModal = () => {
    if (selectedMovimientos.size === 0) {
      toast.error('Seleccione al menos un movimiento');
      return;
    }
    
    const selected = movimientos.filter(m => selectedMovimientos.has(m.id_waya));
    const totalMonto = selected.reduce((sum, m) => sum + (parseFloat(m.monto) || 0), 0);
    const cuits = [...new Set(selected.map(m => m.cuit).filter(Boolean))];
    
    setNewCase({
      titulo: `Caso - ${selected.length} movimiento(s)`,
      descripcion: `Movimientos bajo investigación:\n- Total: $${totalMonto.toLocaleString()}\n- CUIT(s): ${cuits.join(', ') || 'N/A'}`,
      prioridad: 'MEDIA',
      tipo: 'ALERTA'
    });
    setIsCreateCaseOpen(true);
  };

  const handleCreateCase = async () => {
    if (!newCase.titulo.trim()) {
      toast.error('El título es requerido');
      return;
    }

    setSavingCase(true);
    try {
      const selected = movimientos.filter(m => selectedMovimientos.has(m.id_waya));
      const cuits = [...new Set(selected.map(m => m.cuit).filter(Boolean))];
      
      await api.post('/cases', {
        titulo: newCase.titulo,
        descripcion: newCase.descripcion,
        prioridad: newCase.prioridad,
        tipo: newCase.tipo,
        cuentas_relacionadas: cuits,
        movimientos_ids: Array.from(selectedMovimientos).map(String)
      });
      
      toast.success('Caso creado correctamente');
      setIsCreateCaseOpen(false);
      setSelectedMovimientos(new Set());
      setNewCase({ titulo: '', descripcion: '', prioridad: 'MEDIA', tipo: 'ALERTA' });
    } catch (error) {
      console.error('Error creating case:', error);
      toast.error('Error al crear caso');
    } finally {
      setSavingCase(false);
    }
  };

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-slate-50' : ''} p-6 lg:p-8 h-full overflow-auto bg-slate-50`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {isFullscreen && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullscreen(false)}
              style={{ color: '#475569' }}
            >
              <Minimize2 size={16} className="mr-1" />
              Salir
            </Button>
          )}
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">
              Consulta de Movimientos
            </h1>
            <p className="text-slate-600 mt-1">Buscar, analizar y generar alertas sobre transacciones</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setIsFullscreen(!isFullscreen)}
            variant="outline"
            size="sm"
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            style={{ color: '#475569' }}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </Button>
          {selectedMovimientos.size > 0 && (
            <>
              <Button
                onClick={openCreateAlert}
                style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
              >
                <AlertTriangle size={16} className="mr-2" />
                Generar Alerta ({selectedMovimientos.size})
              </Button>
              <Button
                onClick={openCreateCaseModal}
                style={{ backgroundColor: '#7c3aed', color: '#ffffff' }}
              >
                <Briefcase size={16} className="mr-2" />
                Crear Caso ({selectedMovimientos.size})
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="p-6 mb-6 border border-slate-200 bg-white shadow-sm">
        <h2 className="text-lg font-heading font-bold text-slate-900 mb-4">
          Filtros de Búsqueda
        </h2>
        
        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pb-4 border-b border-slate-200">
          <div>
            <Label htmlFor="fecha_desde" className="text-sm font-medium text-slate-700 mb-2 block">
              Fecha Desde
            </Label>
            <Input
              id="fecha_desde"
              type="date"
              value={dateFilters.fecha_desde}
              onChange={(e) => setDateFilters({ ...dateFilters, fecha_desde: e.target.value })}
              className="bg-slate-50"
              style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
            />
          </div>
          <div>
            <Label htmlFor="fecha_hasta" className="text-sm font-medium text-slate-700 mb-2 block">
              Fecha Hasta
            </Label>
            <Input
              id="fecha_hasta"
              type="date"
              value={dateFilters.fecha_hasta}
              onChange={(e) => setDateFilters({ ...dateFilters, fecha_hasta: e.target.value })}
              className="bg-slate-50"
              style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
            />
          </div>
        </div>

        {/* Field Search */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-3">
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              Buscar por Campo
            </Label>
            <Select value={searchField} onValueChange={setSearchField}>
              <SelectTrigger className="bg-slate-50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {searchFields.map(field => (
                  <SelectItem key={field.value} value={field.value}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-6">
            <Label className="text-sm font-medium text-slate-700 mb-2 block">
              Valor
            </Label>
            <Input
              placeholder={`Ingrese ${searchFields.find(f => f.value === searchField)?.label}...`}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
              className="bg-slate-50"
              style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
            />
          </div>
          <div className="md:col-span-3 flex items-end gap-2">
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="flex-1"
              style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
            >
              <Search size={16} className="mr-2" />
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSearchValue('');
              setDateFilters({ fecha_desde: '', fecha_hasta: '' });
              loadRecentMovimientos();
            }}
          >
            Limpiar Filtros
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openImportModal('import')}
            data-testid="import-excel-btn"
            className="text-green-700 border-green-300 hover:bg-green-50"
          >
            <Upload size={14} className="mr-2" />
            Importar Nuevos
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openImportModal('update')}
            data-testid="update-excel-btn"
            className="text-blue-700 border-blue-300 hover:bg-blue-50"
          >
            <FileSpreadsheet size={14} className="mr-2" />
            Actualizar Existentes
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
          >
            <Download size={14} className="mr-2" />
            Exportar CSV
          </Button>
          <ColumnSelector
            section="movimientos"
            onColumnsChange={handleColumnsChange}
            defaultColumns={DEFAULT_COLUMNS}
          />
        </div>
      </Card>

      {/* Selection Controls */}
      {movimientos.length > 0 && (
        <div className="flex items-center gap-4 mb-4 p-3 bg-white border border-slate-200 rounded-lg">
          <span className="text-sm text-slate-600">
            {selectedMovimientos.size} de {movimientos.length} seleccionados
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectAll}>
              <CheckSquare size={14} className="mr-1" />
              Seleccionar todos
            </Button>
            <Button variant="outline" size="sm" onClick={deselectAll}>
              <XSquare size={14} className="mr-1" />
              Deseleccionar
            </Button>
          </div>
          {selectedMovimientos.size > 0 && (
            <div className="flex gap-2 ml-4">
              <button
                onClick={openCreateAlert}
                className="gen-alert-btn"
              >
                <style>{`
                  .gen-alert-btn {
                    background-color: #dc2626 !important;
                    color: #ffffff !important;
                    padding: 6px 12px !important;
                    border-radius: 6px !important;
                    border: none !important;
                    font-size: 14px !important;
                    font-weight: 500 !important;
                    cursor: pointer !important;
                    display: flex !important;
                    align-items: center !important;
                    gap: 4px !important;
                  }
                  .create-case-btn {
                    background-color: #7c3aed !important;
                    color: #ffffff !important;
                    padding: 6px 12px !important;
                    border-radius: 6px !important;
                    border: none !important;
                    font-size: 14px !important;
                    font-weight: 500 !important;
                    cursor: pointer !important;
                    display: flex !important;
                    align-items: center !important;
                    gap: 4px !important;
                  }
                `}</style>
                <AlertTriangle size={14} />
                Generar Alerta
              </button>
              <button
                onClick={openCreateCaseModal}
                className="create-case-btn"
              >
                <Briefcase size={14} />
                Crear Caso
              </button>
            </div>
          )}
        </div>
      )}

      {/* Results Table */}
      <Card className="border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-3 w-10">
                  <Checkbox
                    checked={movimientos.length > 0 && selectedMovimientos.size === movimientos.length}
                    onCheckedChange={(checked) => checked ? selectAll() : deselectAll()}
                  />
                </th>
                {visibleColumns.map(col => (
                  <th 
                    key={col.key} 
                    className={`text-xs uppercase tracking-wider font-medium text-slate-600 p-3 ${col.key === 'monto' ? 'text-right' : 'text-left'}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-slate-600">Cargando movimientos...</p>
                  </td>
                </tr>
              ) : movimientos.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="text-center py-12 text-slate-500">
                    <Database size={48} className="mx-auto text-slate-300 mb-2" />
                    <p>No se encontraron movimientos</p>
                  </td>
                </tr>
              ) : (
                movimientos.map((mov) => {
                  const isSelected = selectedMovimientos.has(mov.id_waya);
                  return (
                    <tr
                      key={mov.id_waya}
                      onClick={() => toggleSelection(mov.id_waya)}
                      className={`cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-blue-50 hover:bg-blue-100' 
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="p-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelection(mov.id_waya)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      {visibleColumns.map(col => {
                        // Renderizar celda según el tipo de dato
                        let value = mov[col.key];
                        let cellClass = "p-3 text-sm text-slate-700";
                        
                        if (col.key === 'id_waya') {
                          cellClass += " font-mono";
                        } else if (col.key === 'fecha') {
                          value = value ? new Date(value).toLocaleDateString('es-AR') : '-';
                          cellClass += " font-mono";
                        } else if (col.key === 'monto') {
                          value = `$${value?.toLocaleString('es-AR') || '0'}`;
                          cellClass = "p-3 text-sm font-mono text-right text-slate-900 font-medium";
                        } else if (col.key === 'cuit') {
                          cellClass += " font-mono";
                        } else if (col.key === 'cvu_destino' || col.key === 'cbu_origen') {
                          value = mov.cbu_cvu_destino || mov[col.key] || '-';
                          cellClass += " font-mono text-xs max-w-[150px] truncate";
                        } else if (col.key === 'movimiento' || col.key === 'descripcion') {
                          cellClass += " max-w-[150px] truncate";
                        }
                        
                        return (
                          <td key={col.key} className={cellClass}>
                            {value || '-'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {movimientos.length > 0 && (
        <div className="mt-4 p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
          <p className="text-sm text-slate-600">
            Mostrando <span className="font-bold text-slate-900">{movimientos.length}</span> movimiento(s)
            {selectedMovimientos.size > 0 && (
              <span className="ml-2 text-blue-600">
                • {selectedMovimientos.size} seleccionado(s)
              </span>
            )}
          </p>
        </div>
      )}

      {/* Modal: Crear Alerta */}
      <Dialog open={isCreateAlertOpen} onOpenChange={setIsCreateAlertOpen}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#0f172a' }}>Generar Alerta desde Movimientos</DialogTitle>
            <DialogDescription style={{ color: '#64748b' }}>
              Crear una alerta basada en {selectedMovimientos.size} movimiento(s) seleccionado(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label style={{ color: '#334155' }}>Título *</Label>
              <Input
                value={newAlert.titulo}
                onChange={(e) => setNewAlert({ ...newAlert, titulo: e.target.value })}
                placeholder="Describe brevemente la alerta..."
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div>
              <Label style={{ color: '#334155' }}>Descripción</Label>
              <Textarea
                value={newAlert.descripcion}
                onChange={(e) => setNewAlert({ ...newAlert, descripcion: e.target.value })}
                placeholder="Detalles adicionales..."
                rows={5}
                style={{ backgroundColor: '#f8fafc', color: '#0f172a' }}
              />
            </div>
            <div>
              <Label style={{ color: '#334155' }}>Severidad</Label>
              <Select
                value={newAlert.severidad}
                onValueChange={(value) => setNewAlert({ ...newAlert, severidad: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map(sev => (
                    <SelectItem key={sev} value={sev}>{sev}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateAlertOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateAlert}
              disabled={savingAlert}
              style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
            >
              {savingAlert ? 'Creando...' : 'Crear Alerta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Crear Caso */}
      <Dialog open={isCreateCaseOpen} onOpenChange={setIsCreateCaseOpen}>
        <DialogContent style={{ backgroundColor: '#ffffff', maxWidth: '500px' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#0f172a' }}>
              <Briefcase className="text-purple-600" size={24} />
              Crear Caso desde Movimientos
            </DialogTitle>
            <DialogDescription style={{ color: '#64748b' }}>
              Crear un caso de investigación basado en {selectedMovimientos.size} movimiento(s) seleccionado(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="case-titulo" style={{ color: '#334155' }}>Título del Caso *</Label>
              <Input
                id="case-titulo"
                value={newCase.titulo}
                onChange={(e) => setNewCase({ ...newCase, titulo: e.target.value })}
                placeholder="Título descriptivo del caso"
                style={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', color: '#0f172a' }}
              />
            </div>
            
            <div>
              <Label htmlFor="case-descripcion" style={{ color: '#334155' }}>Descripción</Label>
              <Textarea
                id="case-descripcion"
                value={newCase.descripcion}
                onChange={(e) => setNewCase({ ...newCase, descripcion: e.target.value })}
                rows={3}
                style={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', color: '#0f172a' }}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label style={{ color: '#334155' }}>Tipo de Caso</Label>
                <Select value={newCase.tipo} onValueChange={(v) => setNewCase({ ...newCase, tipo: v })}>
                  <SelectTrigger style={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', color: '#0f172a' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FRAUDE">Fraude</SelectItem>
                    <SelectItem value="SOPORTE">Soporte</SelectItem>
                    <SelectItem value="ALERTA">Alerta</SelectItem>
                    <SelectItem value="COMPLIANCE">Compliance</SelectItem>
                    <SelectItem value="OTRO">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label style={{ color: '#334155' }}>Prioridad</Label>
                <Select value={newCase.prioridad} onValueChange={(v) => setNewCase({ ...newCase, prioridad: v })}>
                  <SelectTrigger style={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0', color: '#0f172a' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CRITICA">Crítica</SelectItem>
                    <SelectItem value="ALTA">Alta</SelectItem>
                    <SelectItem value="MEDIA">Media</SelectItem>
                    <SelectItem value="BAJA">Baja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
              <p className="text-sm font-medium text-purple-800">
                Movimientos: {selectedMovimientos.size}
              </p>
              <p className="text-xs text-purple-600 mt-1">
                IDs: {Array.from(selectedMovimientos).slice(0, 5).join(', ')}{selectedMovimientos.size > 5 ? '...' : ''}
              </p>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsCreateCaseOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateCase}
              disabled={savingCase || !newCase.titulo.trim()}
              style={{ backgroundColor: '#7c3aed', color: '#ffffff' }}
            >
              {savingCase ? 'Creando...' : 'Crear Caso'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Importar/Actualizar Excel */}
      <Dialog open={isImportOpen} onOpenChange={closeImportModal}>
        <DialogContent style={{ backgroundColor: '#ffffff', maxWidth: '700px' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#0f172a' }}>
              <FileSpreadsheet className={importMode === 'import' ? "text-green-600" : "text-blue-600"} size={24} />
              {importMode === 'import' ? 'Importar Nuevos Movimientos' : 'Actualizar Movimientos Existentes'}
            </DialogTitle>
            <DialogDescription style={{ color: '#64748b' }}>
              {importMode === 'import' 
                ? 'Solo inserta registros nuevos. Si el ID ya existe, lo omite.'
                : 'Solo actualiza registros existentes. Si el ID no existe, lo omite.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Info box */}
            <div className={`p-3 rounded-lg text-sm ${importMode === 'import' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
              <p className={`font-medium mb-1 ${importMode === 'import' ? 'text-green-800' : 'text-blue-800'}`}>
                {importMode === 'import' ? 'Modo: INSERTAR NUEVOS' : 'Modo: ACTUALIZAR EXISTENTES'}
              </p>
              <ul className={`text-xs space-y-0.5 ${importMode === 'import' ? 'text-green-700' : 'text-blue-700'}`}>
                <li>• <strong>Clave:</strong> Columna &quot;Id&quot; del Excel = campo id_waya</li>
                {importMode === 'import' && <li>• Los nuevos registros se marcan con &quot;ALERTG-IMP&quot;</li>}
                <li>• Referencia = &quot;Transferencia&quot; → EGRESO</li>
                <li>• Referencia con más texto → INGRESO</li>
              </ul>
            </div>

            {/* File input */}
            <div className="space-y-2">
              <Label style={{ color: '#334155' }}>Archivo Excel</Label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="excel-file-input"
                  data-testid="excel-file-input"
                />
                <label
                  htmlFor="excel-file-input"
                  className={`flex-1 flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${importMode === 'import' ? 'border-green-300 hover:border-green-400 hover:bg-green-50' : 'border-blue-300 hover:border-blue-400 hover:bg-blue-50'}`}
                >
                  {importFile ? (
                    <div className="flex items-center gap-2 text-green-700">
                      <FileSpreadsheet size={20} />
                      <span className="text-sm font-medium">{importFile.name}</span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setImportFile(null);
                          setImportResult(null);
                          setPreviewData(null);
                        }}
                        className="ml-2 p-1 hover:bg-red-100 rounded"
                      >
                        <X size={14} className="text-red-500" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-slate-500">
                      <Upload size={24} className="mx-auto mb-1" />
                      <span className="text-sm">Click para seleccionar archivo</span>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Vista Previa del Excel */}
            {loadingPreview && (
              <div className="flex items-center justify-center p-4">
                <RefreshCw className="animate-spin text-blue-500 mr-2" size={20} />
                <span className="text-sm text-slate-500">Cargando vista previa...</span>
              </div>
            )}
            
            {previewData && !loadingPreview && (
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-sm text-slate-800 flex items-center gap-2">
                    <Eye size={16} className="text-blue-500" />
                    Vista Previa ({previewData.total_rows} filas detectadas)
                  </p>
                  {previewData.is_valid_format ? (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <Check size={12} />
                      Formato válido
                    </span>
                  ) : (
                    <span className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle size={12} />
                      Columnas faltantes
                    </span>
                  )}
                </div>
                
                {/* Columnas detectadas */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {previewData.found_columns?.map((col, idx) => (
                    <span key={idx} className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">
                      ✓ {col}
                    </span>
                  ))}
                  {previewData.missing_columns?.map((col, idx) => (
                    <span key={idx} className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                      ⚠ {col}
                    </span>
                  ))}
                </div>
                
                {/* Tabla de preview */}
                <div className="overflow-x-auto max-h-[200px] overflow-y-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-200">
                        <th className="p-1 border border-slate-300 text-left">#</th>
                        {previewData.headers?.slice(0, 6).map((h, idx) => (
                          <th key={idx} className="p-1 border border-slate-300 text-left truncate max-w-[100px]">
                            {h}
                          </th>
                        ))}
                        {previewData.headers?.length > 6 && (
                          <th className="p-1 border border-slate-300 text-center">...</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.preview_rows?.map((row, rowIdx) => (
                        <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="p-1 border border-slate-200 text-slate-400">{rowIdx + 1}</td>
                          {previewData.headers?.slice(0, 6).map((h, colIdx) => (
                            <td key={colIdx} className="p-1 border border-slate-200 truncate max-w-[100px]">
                              {row[h] !== null && row[h] !== undefined ? String(row[h]) : '-'}
                            </td>
                          ))}
                          {previewData.headers?.length > 6 && (
                            <td className="p-1 border border-slate-200 text-center text-slate-400">...</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {previewData.preview_rows?.length < previewData.total_rows && (
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Mostrando {previewData.preview_rows.length} de {previewData.total_rows} filas
                  </p>
                )}
              </div>
            )}

            {/* Import result */}
            {importResult && (
              <div className={`p-3 rounded-lg text-sm ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {importResult.success ? (
                  <>
                    <p className="font-medium text-green-800">
                      {importMode === 'import' ? 'Importación exitosa' : 'Actualización exitosa'}
                    </p>
                    <ul className="text-green-700 text-xs mt-1">
                      {importMode === 'import' ? (
                        <>
                          <li>• Nuevos insertados: {importResult.inserted}</li>
                          <li>• Omitidos (ya existen): {importResult.skipped}</li>
                        </>
                      ) : (
                        <>
                          <li>• Actualizados: {importResult.updated}</li>
                          <li>• Omitidos (no existen): {importResult.skipped}</li>
                        </>
                      )}
                      <li>• Total procesados: {importResult.total_processed}</li>
                    </ul>
                    {importResult.errors_count > 0 && (
                      <p className="text-amber-600 mt-1">
                        ⚠ {importResult.errors_count} error(es)
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-red-700">{importResult.message || 'Error en el proceso'}</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeImportModal}>
              Cerrar
            </Button>
            <Button
              onClick={handleImportExcel}
              disabled={importing || !importFile}
              style={{ backgroundColor: importMode === 'import' ? '#16a34a' : '#2563eb', color: '#ffffff' }}
              data-testid="import-submit-btn"
            >
              {importing ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  {importMode === 'import' ? 'Importando...' : 'Actualizando...'}
                </>
              ) : (
                <>
                  {importMode === 'import' ? <Upload size={16} className="mr-2" /> : <FileSpreadsheet size={16} className="mr-2" />}
                  {importMode === 'import' ? 'Importar Nuevos' : 'Actualizar Existentes'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Movimientos;
